import { defineStore } from 'pinia'
import { nanoid } from 'nanoid'
import type { ToastOptions } from '../types/Toast'
export const useToastStore = defineStore('toast', {
  state: () => ({
    items: [] as Required<ToastOptions>[],
    timeouts: new Map<string, number>(),
    maxVisible: 5 as number,
  }),
  actions: {
    show(opts: ToastOptions) {
      const id = opts.id ?? nanoid()
      const duration = opts.duration ?? 4000
      const normalized: Required<ToastOptions> = {
        id,
        kind: opts.kind ?? 'info',
        title: opts.title ?? '',
        message: opts.message,
        duration,
        dismissible: opts.dismissible ?? true,
        action: opts.action ?? null,
      }
      // optional: prevent same toast spamming back-to-back
      const last = this.items[0]
      if (last && last.message === normalized.message && last.kind === normalized.kind) return id
      // queueing: keep most recent visible
      this.items.unshift(normalized)
      if (this.items.length > this.maxVisible) this.items.pop()
      if (duration > 0) {
        const handle = window.setTimeout(() => this.dismiss(id), duration)
        this.timeouts.set(id, handle)
      }
      return id
    },
    dismiss(id: string) {
      const idx = this.items.findIndex(t => t.id === id)
      if (idx !== -1) this.items.splice(idx, 1)
      const handle = this.timeouts.get(id)
      if (handle) {
        clearTimeout(handle)
        this.timeouts.delete(id)
      }
    },
    clearAll() {
      this.items = []
      this.timeouts.forEach(h => clearTimeout(h))
      this.timeouts.clear()
    },
    // sugar
    success(message: string, extra: Partial<ToastOptions> = {}) {
      return this.show({ kind: 'success', message, ...extra })
    },
    error(message: string, extra: Partial<ToastOptions> = {}) {
      return this.show({ kind: 'error', message, ...extra })
    },
    info(message: string, extra: Partial<ToastOptions> = {}) {
      return this.show({ kind: 'info', message, ...extra })
    },
    warning(message: string, extra: Partial<ToastOptions> = {}) {
      return this.show({ kind: 'warning', message, ...extra })
    },
  },
})
